<template>
  <div class="campo-input-container mb-3">
    <div v-if="!hideLabel" class="d-flex justify-content-between align-items-center mb-2">
      <label
        :for="id"
        class="form-label mb-0"
        :class="{ 'is-required': isObrigatorio }"
      >
        {{ label }}
        <i
          v-if="labelInfo"
          class="bi bi-info-circle text-primary ms-1"
          :title="labelInfo"
          style="cursor: help; font-size: 0.85em;"
        ></i>
      </label>
      <slot name="label-extra" />
    </div>

    <component
      :is="tipoComponente"
      :id="id"
      class="form-control"
      :class="{ 'is-invalid': errorMessage, 'text-end': isFormattedNumber }"
      :value="displayValue"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @paste="handlePaste"
      :type="tipoInputHTML"
      :inputmode="isFormattedNumber ? 'numeric' : null"
      :rows="tipoComponente === 'textarea' ? 3 : null"
      v-bind="$attrs"
    />
    <div v-if="descricaoInformativa && !errorMessage" class="form-text mt-1 shadow-sm px-2 py-1 rounded bg-body-tertiary border border-secondary-subtle d-inline-block text-body-emphasis">
      <i class="bi bi-info-circle me-1 text-primary"></i> {{ descricaoInformativa }}
    </div>
    <div v-if="errorMessage" class="invalid-feedback">{{ errorMessage }}</div>
  </div>
</template>

<script setup>
import { computed, inject, ref } from 'vue'

// Estado local para controle de foco e valor cru durante digitação
const rawInputValue = ref('')
const isFocused = ref(false)

defineOptions({
  inheritAttrs: false
})

const props = defineProps({
  id: { type: String, required: true },
  label: { type: String, default: '' },
  modelValue: { type: [String, Number, null], default: '' }, // Agora aceita null explicitamente
  type: { type: String, default: 'text' },
  required: { type: [Boolean, String, Number], default: false },
  hideLabel: { type: Boolean, default: false },
  error: { type: String, default: '' },
  descricaoInformativa: { type: String, default: '' },

  // GUARDIÃO: Nova prop dinâmica
  casasDecimais: { type: Number, default: null },

  // Nova prop para formatação de inteiros dinâmicos (milhares, e decimais apenas se existirem)
  dynamicDecimals: { type: Boolean, default: false },

  // GUARDIÃO: Texto informativo exibido como tooltip no ícone de informação ao lado do label
  labelInfo: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue', 'blur'])

const formErrors = inject('formErrors', ref({}))
const errorMessage = computed(() => props.error || formErrors.value[props.id] || '')

const isObrigatorio = computed(() => {
  return props.required === true || props.required === 'true' || props.required === 'required' || props.required === ''
})

const tipoComponente = computed(() => props.type === 'textarea' ? 'textarea' : 'input')

const isFormattedNumber = computed(() => {
  return ['moeda', 'percentual', 'decimal'].includes(props.type) || props.dynamicDecimals
})

const tipoInputHTML = computed(() => isFormattedNumber.value ? 'text' : props.type)

const resolvedCasasDecimais = computed(() => {
  if (props.casasDecimais !== null && props.casasDecimais !== undefined) {
    return props.casasDecimais
  }
  if (props.type === 'moeda') return 2
  if (props.type === 'percentual') return 2
  if (props.type === 'decimal') return 4
  return 0
})

const displayValue = computed(() => {
  // Se está com foco, mostra o valor cru para edição livre (sem formatação)
  if (isFocused.value) {
    return rawInputValue.value
  }

  if (isFormattedNumber.value) {
    // GUARDIÃO FIX: Se for vazio ou null, mostra vazio. Não inventa zeros!
    if (props.modelValue === null || props.modelValue === undefined || props.modelValue === '') {
      return ''
    }

    const valor = Number(props.modelValue)
    if (isNaN(valor)) return ''

    if (props.dynamicDecimals) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4
      }).format(valor)
    }

    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: resolvedCasasDecimais.value,
      maximumFractionDigits: resolvedCasasDecimais.value
    }).format(valor)
  }
  return props.modelValue
})

const handleInput = (event) => {
  if (isFormattedNumber.value) {
    // Guarda o valor EXATAMENTE como o usuário digitou, sem qualquer sanitização
    // O sistema NÃO interfere na digitação — cursor, delete, backspace, tudo livre
    rawInputValue.value = event.target.value

    // Tenta extrair um número para manter o v-model sincronizado com validações
    // Remove pontos de milhar e troca vírgula decimal por ponto
    const cleaned = event.target.value.replace(/\./g, '').replace(',', '.')
    const num = parseFloat(cleaned)
    if (!isNaN(num) && cleaned !== '' && cleaned !== '.') {
      emit('update:modelValue', num)
    } else if (event.target.value === '') {
      emit('update:modelValue', null)
    } else {
      // Input inválido (ex: "abc") — emite null para sincronizar o v-model
      emit('update:modelValue', null)
    }

    return
  }

  emit('update:modelValue', event.target.value)
}

const handleFocus = () => {
  if (isFormattedNumber.value) {
    if (props.modelValue === null || props.modelValue === undefined || props.modelValue === '') {
      rawInputValue.value = ''
    } else {
      const valor = Number(props.modelValue)
      if (!isNaN(valor)) {
        // Mantém o valor formatado visível (ex: "10.000,00") em vez de converter para cru
        // O usuário vê o número exatamente como estava antes do foco
        rawInputValue.value = new Intl.NumberFormat('pt-BR', {
          minimumFractionDigits: props.dynamicDecimals ? 0 : resolvedCasasDecimais.value,
          maximumFractionDigits: props.dynamicDecimals ? 4 : resolvedCasasDecimais.value
        }).format(valor)
      } else {
        rawInputValue.value = ''
      }
    }
    isFocused.value = true

    // NÃO seleciona o texto automaticamente — respeita a posição do cursor onde o usuário clicou
    // O usuário pode usar Ctrl+A para selecionar tudo se desejar
  }
}

const handleBlur = () => {
  emit('blur')
  if (isFormattedNumber.value) {
    isFocused.value = false

    const raw = rawInputValue.value
    if (!raw || raw.trim() === '') {
      emit('update:modelValue', null)
      return
    }

    // Sanitização completa no blur:
    // 1. Remove separadores de milhar (pontos)
    // 2. Troca a primeira vírgula por ponto (separador decimal)
    // 3. Remove qualquer caractere não numérico restante
    // Ex: "100.000,00" → "100000,00" → "100000.00" → 100000
    const cleaned = raw
      .replace(/\./g, '') // remove pontos de milhar
      .replace(',', '.') // troca primeira vírgula por ponto decimal
      .replace(/[^\d.-]/g, '') // remove caracteres não numéricos
      .replace(/-/g, '') // remove sinal negativo (não suportamos negativos)

    const numericValue = parseFloat(cleaned)

    if (isNaN(numericValue) || !isFinite(numericValue)) {
      emit('update:modelValue', null)
    } else {
      emit('update:modelValue', numericValue)
    }
  }
}

/**
 * Handler de @paste para inputs type="date".
 * O native <input type="date"> não aceita paste de datas em formato brasileiro (DD/MM/YYYY)
 * e o padrão :value + @input do componente controlado interfere no paste de ISO (YYYY-MM-DD).
 *
 * Esta função:
 * 1. Previne o paste padrão do browser
 * 2. Lê o texto da área de transferência
 * 3. Tenta parsear nos formatos: YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY, YYYY/MM/DD
 * 4. Converte para ISO (YYYY-MM-DD) e aplica no input + model
 */
const handlePaste = (event) => {
  if (props.type !== 'date') return

  event.preventDefault()

  const pastedText = (event.clipboardData || window.clipboardData).getData('text/plain')
  if (!pastedText) return

  const trimmed = pastedText.trim()
  let isoDate = null

  // YYYY-MM-DD (ISO) ou YYYY/MM/DD
  let match = trimmed.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/)
  if (match) {
    isoDate = `${match[1]}-${match[2]}-${match[3]}`
  }

  // DD/MM/YYYY ou DD-MM-YYYY (formato brasileiro)
  if (!isoDate) {
    match = trimmed.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/)
    if (match) {
      isoDate = `${match[3]}-${match[2]}-${match[1]}`
    }
  }

  // DD/MM/YY ou DD-MM-YY (ano abreviado)
  if (!isoDate) {
    match = trimmed.match(/^(\d{2})[-/](\d{2})[-/](\d{2})$/)
    if (match) {
      const prefix = parseInt(match[3], 10) >= 50 ? '19' : '20'
      isoDate = `${prefix}${match[3]}-${match[2]}-${match[1]}`
    }
  }

  if (isoDate) {
    event.target.value = isoDate
    emit('update:modelValue', isoDate)
  }
}
</script>

<style scoped>
.form-label { font-weight: 400 !important; }
.form-label.is-required::after { content: " *"; color: #dc3545; font-weight: bold; }
.campo-input-container { display: flex; flex-direction: column; }
.text-end { text-align: right; }
</style>
